'use client';

import React, { ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
    AlignJustify,
    ChevronDown,
    Facebook,
    Globe,
    House,
    Instagram,
    Mail,
    MapPinned,
    Phone,
    Search,
    X,
} from 'lucide-react';

import { DesktopDropDownMenu } from './DesktopDropDownMenu';
import {
    DropdownItem,
    HeaderNavMenuItem,
    LinkProps,
    MegaNavContactDetails,
    MegaNavProps,
    MegaNavTransparentBackgroundConfig,
    MegaNavTopLevelActiveStyle,
    MegaNavTopLevelLinkStyleOverrides,
    MegaNavUtilityDisplayMode,
    MegaNavUtilityLink,
} from './MegaNav.types';
import {
    layoutTokens,
    logoLayoutTokens,
    themeTokens,
    topLevelLinkStyleTokens,
} from './MegaNav.tokens';
import MegaNavSearch from './MegaNavSearch';
import { buildTelHref } from '../../../lib/utils/phone';

type ThemeTokenSet = typeof themeTokens.light;
type LayoutKey = keyof typeof layoutTokens;
type TopLevelLinkStyleSet = Required<MegaNavTopLevelLinkStyleOverrides>;
type ResolvedTransparentBackgroundConfig = Required<
    Pick<MegaNavTransparentBackgroundConfig, 'enabled' | 'mobile' | 'desktop' | 'scrollThreshold'>
> &
    Omit<MegaNavTransparentBackgroundConfig, 'enabled' | 'mobile' | 'desktop' | 'scrollThreshold'>;

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';
const DEFAULT_TRANSPARENT_SCROLL_THRESHOLD = 24;

const resolveTransparentBackgroundConfig = (
    transparentBackground?: boolean | MegaNavTransparentBackgroundConfig,
): ResolvedTransparentBackgroundConfig => {
    if (!transparentBackground) {
        return {
            enabled: false,
            mobile: true,
            desktop: true,
            scrollThreshold: DEFAULT_TRANSPARENT_SCROLL_THRESHOLD,
        };
    }

    if (typeof transparentBackground === 'boolean') {
        return {
            enabled: transparentBackground,
            mobile: true,
            desktop: true,
            scrollThreshold: DEFAULT_TRANSPARENT_SCROLL_THRESHOLD,
        };
    }

    return {
        ...transparentBackground,
        enabled: transparentBackground.enabled ?? true,
        mobile: transparentBackground.mobile ?? true,
        desktop: transparentBackground.desktop ?? true,
        scrollThreshold:
            transparentBackground.scrollThreshold ?? DEFAULT_TRANSPARENT_SCROLL_THRESHOLD,
    };
};

const getTopLevelLinkStyles = (
    theme: keyof typeof topLevelLinkStyleTokens,
    overrides?: MegaNavTopLevelLinkStyleOverrides,
): TopLevelLinkStyleSet => {
    const base = topLevelLinkStyleTokens[theme];

    return {
        link: { ...base.link, ...overrides?.link },
        label: { ...base.label, ...overrides?.label },
        activeUnderline: { ...base.activeUnderline, ...overrides?.activeUnderline },
        activeBackground: {
            ...base.activeBackground,
            ...overrides?.activeBackground,
        },
    };
};

const getUtilityIcon = (icon?: MegaNavUtilityLink['icon']) => {
    switch (icon) {
        case 'instagram':
            return <Instagram size={15} aria-hidden="true" />;
        case 'facebook':
            return <Facebook size={15} aria-hidden="true" />;
        case 'google':
            return <Globe size={15} aria-hidden="true" />;
        case 'maps':
            return <MapPinned size={15} aria-hidden="true" />;
        case 'phone':
            return <Phone size={15} aria-hidden="true" />;
        case 'email':
            return <Mail size={15} aria-hidden="true" />;
        case 'address':
            return <House size={15} aria-hidden="true" />;
        default:
            return null;
    }
};

const normalizePathname = (pathname?: string | null) => {
    if (!pathname) return '/';

    const [pathWithoutQuery] = pathname.split(/[?#]/);
    const normalized = pathWithoutQuery.replace(/\/+$/, '');

    return normalized || '/';
};

const getRouteMatchScore = (currentPathname: string, targetPathname?: string) => {
    if (!targetPathname || targetPathname === '#') return 0;
    if (!targetPathname.startsWith('/')) return 0;

    const current = normalizePathname(currentPathname);
    const target = normalizePathname(targetPathname);

    if (target === '/') return current === '/' ? 10000 : 0;
    if (current === target) return 10000 + target.length;
    if (current.startsWith(`${target}/`)) return 1000 + target.length;

    return 0;
};

const getMenuItemRouteScore = (currentPathname: string, item: HeaderNavMenuItem) => {
    let bestScore = getRouteMatchScore(currentPathname, item.href);

    item.children?.forEach((group) => {
        bestScore = Math.max(
            bestScore,
            getRouteMatchScore(currentPathname, group.headingUrl ?? group.href),
        );

        group.submenu?.forEach((subItem) => {
            bestScore = Math.max(bestScore, getRouteMatchScore(currentPathname, subItem.href));
        });
    });

    return bestScore;
};

const getActiveMenuIndex = (currentPathname: string, menuData: HeaderNavMenuItem[]) => {
    let activeIndex: number | null = null;
    let activeScore = 0;

    menuData.forEach((item, index) => {
        const score = getMenuItemRouteScore(currentPathname, item);

        if (score > activeScore) {
            activeIndex = index;
            activeScore = score;
        }
    });

    return activeIndex;
};

const getWindowPathname = () => {
    if (typeof window === 'undefined') return '/';

    return normalizePathname(window.location.pathname);
};

const SearchFallback = ({ tokens, className }: { tokens: ThemeTokenSet; className?: string }) => (
    <div className={clsx('relative', className)}>
        <Search
            size={16}
            className={clsx(
                'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2',
                tokens.textMuted,
            )}
            aria-hidden="true"
        />
        <input
            type="search"
            aria-label="Global search"
            placeholder="Search"
            className={clsx(
                'h-9 w-full rounded-full border px-10 text-base outline-none transition-colors',
                tokens.border,
                tokens.textPrimary,
                tokens.searchBg,
                tokens.searchPlaceholder,
            )}
        />
    </div>
);

const UtilityLinkItem = ({
    item,
    Link,
    tokens,
    onClick,
    displayMode = 'icon-label',
    textClassName,
}: {
    item: MegaNavUtilityLink;
    Link: ComponentType<LinkProps>;
    tokens: ThemeTokenSet;
    onClick?: () => void;
    displayMode?: MegaNavUtilityDisplayMode;
    textClassName?: string;
}) => {
    const isIconOnly = displayMode === 'icon-only';
    const icon = item.icon ? getUtilityIcon(item.icon) : null;
    const href =
        item.icon === 'phone' || item.href.toLowerCase().startsWith('tel:')
            ? buildTelHref(item.href) || item.href
            : item.href;

    return (
        <Link
            href={href}
            onClick={onClick}
            className={clsx(
                'inline-flex items-center justify-center gap-1.5 rounded-full transition-colors',
                isIconOnly ? 'h-7 w-7 p-0' : 'px-2.5 py-1.5 text-[11px]',
                textClassName ?? tokens.textSecondary,
                tokens.hoverBg,
            )}
            target={item.external ? '_blank' : undefined}
            rel={item.external ? 'noreferrer noopener' : undefined}
            aria-label={item.ariaLabel ?? item.label}
        >
            {icon}
            {!isIconOnly ? <span className="leading-none">{item.label}</span> : null}
        </Link>
    );
};

const buildContactLinks = (contactDetails?: MegaNavContactDetails): MegaNavUtilityLink[] => {
    if (!contactDetails) return [];

    const items: MegaNavUtilityLink[] = [];

    if (contactDetails.phone) {
        items.push({
            label: contactDetails.phoneLabel ?? contactDetails.phone,
            href: buildTelHref(contactDetails.phone) || `tel:${contactDetails.phone}`,
            icon: 'phone',
            ariaLabel: `Call ${contactDetails.phoneLabel ?? contactDetails.phone}`,
        });
    }

    if (contactDetails.email) {
        items.push({
            label: contactDetails.emailLabel ?? contactDetails.email,
            href: `mailto:${contactDetails.email}`,
            icon: 'email',
            ariaLabel: `Email ${contactDetails.emailLabel ?? contactDetails.email}`,
        });
    }

    if (contactDetails.address) {
        items.push({
            label: contactDetails.addressLabel ?? contactDetails.address,
            href: contactDetails.addressHref ?? '#',
            icon: 'address',
            external: !!contactDetails.addressHref,
            ariaLabel: `View address for ${contactDetails.addressLabel ?? contactDetails.address}`,
        });
    }

    return items;
};

const MenuItem = ({
    label,
    href,
    icon,
    children,
    active,
    isRouteActive,
    onToggle,
    Link,
    tokens,
    topLevelActiveStyle,
    topLevelActiveColor,
    topLevelLinkStyles,
}: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    children?: DropdownItem[];
    active: boolean;
    isRouteActive: boolean;
    onToggle?: () => void;
    Link: ComponentType<LinkProps>;
    tokens: ThemeTokenSet;
    topLevelActiveStyle: MegaNavTopLevelActiveStyle;
    topLevelActiveColor?: string;
    topLevelLinkStyles: TopLevelLinkStyleSet;
}) => {
    const useActiveBackground = isRouteActive && topLevelActiveStyle === 'background';
    const useActiveUnderline = isRouteActive && topLevelActiveStyle === 'underline';
    const hasChildren = !!children?.length;
    const activeColorStyle =
        isRouteActive && topLevelActiveColor ? { color: topLevelActiveColor } : null;
    const activeUnderlineColorStyle =
        useActiveUnderline && topLevelActiveColor
            ? { color: topLevelActiveColor, borderBottomColor: topLevelActiveColor }
            : null;

    return (
        <li onMouseEnter={onToggle} className="h-full">
            <Link
                href={href}
                className={clsx(
                    'flex h-full min-w-0 items-center gap-1.5 rounded-md px-1.5 py-2 text-sm font-medium transition-colors duration-200 lg:px-2.5',
                    active || useActiveBackground ? tokens.activeBg : tokens.hoverBg,
                    tokens.textSecondary,
                )}
                style={{
                    ...topLevelLinkStyles.link,
                    ...(useActiveBackground ? topLevelLinkStyles.activeBackground : null),
                    ...activeColorStyle,
                }}
                aria-label={label}
            >
                {icon && <span className="shrink-0">{icon}</span>}
                <span
                    className={clsx(
                        'inline-flex min-w-0 items-center border-b-2 pb-0.5',
                        useActiveUnderline ? 'border-current' : 'border-transparent',
                    )}
                    style={{
                        ...topLevelLinkStyles.label,
                        ...(useActiveUnderline ? topLevelLinkStyles.activeUnderline : null),
                        ...activeUnderlineColorStyle,
                    }}
                >
                    {label}
                </span>

                {hasChildren && (
                    <span
                        className={clsx(
                            'hidden shrink-0 transition-transform duration-300 md:inline-flex',
                            active ? 'rotate-180' : 'rotate-0',
                            tokens.textSecondary,
                        )}
                        style={activeColorStyle ?? undefined}
                        aria-hidden="true"
                    >
                        <ChevronDown size={13} />
                    </span>
                )}
            </Link>
        </li>
    );
};

const MegaMenu = ({
    menuData,
    handleToggle,
    activeIndex,
    routeActiveIndex,
    Link,
    tokens,
    topLevelActiveStyle,
    topLevelActiveColor,
    topLevelLinkStyles,
}: {
    menuData: HeaderNavMenuItem[];
    handleToggle: (index: number) => void;
    activeIndex: number | null;
    routeActiveIndex: number | null;
    Link: ComponentType<LinkProps>;
    tokens: ThemeTokenSet;
    topLevelActiveStyle: MegaNavTopLevelActiveStyle;
    topLevelActiveColor?: string;
    topLevelLinkStyles: TopLevelLinkStyleSet;
}) => (
    <nav className="h-full min-w-0" aria-label="Primary navigation">
        <ul className="flex h-full min-w-0 flex-nowrap items-center gap-0.5 lg:gap-1">
            {menuData?.map((item, index) => (
                <MenuItem
                    key={index}
                    {...item}
                    active={activeIndex === index}
                    isRouteActive={routeActiveIndex === index}
                    onToggle={() => handleToggle(index)}
                    Link={Link}
                    tokens={tokens}
                    topLevelActiveStyle={topLevelActiveStyle}
                    topLevelActiveColor={topLevelActiveColor}
                    topLevelLinkStyles={topLevelLinkStyles}
                />
            ))}
        </ul>
    </nav>
);

const MobileUtilitySection = ({
    socialLinks,
    utilityLinks,
    contactLinks,
    Link,
    tokens,
    onNavigate,
    utilityDisplayMode,
}: {
    socialLinks: MegaNavUtilityLink[];
    utilityLinks: MegaNavUtilityLink[];
    contactLinks: MegaNavUtilityLink[];
    Link: ComponentType<LinkProps>;
    tokens: ThemeTokenSet;
    onNavigate: () => void;
    utilityDisplayMode: MegaNavUtilityDisplayMode;
}) => {
    const hasAnything = socialLinks.length || utilityLinks.length || contactLinks.length;

    if (!hasAnything) return null;

    return (
        <div className={clsx('mt-6 space-y-5 border-t pt-5', tokens.border)}>
            {!!contactLinks.length && (
                <div>
                    <div
                        className={clsx(
                            'mb-2 text-xs font-semibold uppercase tracking-wide',
                            tokens.textMuted,
                        )}
                    >
                        Contact
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {contactLinks.map((item) => (
                            <UtilityLinkItem
                                key={`${item.label}-${item.href}`}
                                item={item}
                                Link={Link}
                                tokens={tokens}
                                onClick={onNavigate}
                                displayMode={utilityDisplayMode}
                            />
                        ))}
                    </div>
                </div>
            )}

            {!!utilityLinks.length && (
                <div>
                    <div
                        className={clsx(
                            'mb-2 text-xs font-semibold uppercase tracking-wide',
                            tokens.textMuted,
                        )}
                    >
                        Quick links
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {utilityLinks.map((item) => (
                            <UtilityLinkItem
                                key={`${item.label}-${item.href}`}
                                item={item}
                                Link={Link}
                                tokens={tokens}
                                onClick={onNavigate}
                                displayMode={utilityDisplayMode}
                            />
                        ))}
                    </div>
                </div>
            )}

            {!!socialLinks.length && (
                <div>
                    <div
                        className={clsx(
                            'mb-2 text-xs font-semibold uppercase tracking-wide',
                            tokens.textMuted,
                        )}
                    >
                        Social
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {socialLinks.map((item) => (
                            <UtilityLinkItem
                                key={`${item.label}-${item.href}`}
                                item={item}
                                Link={Link}
                                tokens={tokens}
                                onClick={onNavigate}
                                displayMode={utilityDisplayMode}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const MobileMegaMenu = ({
    menuData,
    Link,
    setIsDrawerOpen,
    tokens,
    routeActiveIndex,
    topLevelActiveStyle,
    topLevelActiveColor,
    topLevelLinkStyles,
}: {
    menuData: HeaderNavMenuItem[];
    Link: ComponentType<LinkProps>;
    setIsDrawerOpen: (v: boolean) => void;
    tokens: ThemeTokenSet;
    routeActiveIndex: number | null;
    topLevelActiveStyle: MegaNavTopLevelActiveStyle;
    topLevelActiveColor?: string;
    topLevelLinkStyles: TopLevelLinkStyleSet;
}) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    return (
        <nav className="space-y-2" aria-label="Mobile navigation">
            {menuData?.map((item, index) => {
                const isOpen = openIndex === index;
                const isRouteActive = routeActiveIndex === index;
                const useActiveBackground = isRouteActive && topLevelActiveStyle === 'background';
                const useActiveUnderline = isRouteActive && topLevelActiveStyle === 'underline';
                const activeColorStyle =
                    isRouteActive && topLevelActiveColor ? { color: topLevelActiveColor } : null;
                const activeUnderlineColorStyle =
                    useActiveUnderline && topLevelActiveColor
                        ? {
                              color: topLevelActiveColor,
                              borderBottomColor: topLevelActiveColor,
                          }
                        : null;

                return (
                    <div key={index} className={clsx('border-b py-1', tokens.border)}>
                        <div
                            className={clsx(
                                'flex w-full items-center justify-between gap-2 py-2',
                                tokens.textPrimary,
                            )}
                        >
                            <Link
                                href={item.href ?? '#'}
                                className={clsx(
                                    'block min-w-0 flex-1 rounded-md px-2 py-2 text-sm font-semibold',
                                    tokens.textPrimary,
                                )}
                                style={{
                                    ...topLevelLinkStyles.link,
                                    ...(useActiveBackground
                                        ? topLevelLinkStyles.activeBackground
                                        : null),
                                    ...activeColorStyle,
                                }}
                                onClick={() => setIsDrawerOpen(false)}
                            >
                                <span
                                    className={clsx(
                                        'inline-flex items-center border-b-2 pb-0.5',
                                        useActiveUnderline
                                            ? 'border-current'
                                            : 'border-transparent',
                                    )}
                                    style={{
                                        ...topLevelLinkStyles.label,
                                        ...(useActiveUnderline
                                            ? topLevelLinkStyles.activeUnderline
                                            : null),
                                        ...activeUnderlineColorStyle,
                                    }}
                                >
                                    {item.label}
                                </span>
                            </Link>

                            {item.children ? (
                                <button
                                    type="button"
                                    className={clsx(
                                        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors',
                                        tokens.hoverBg,
                                    )}
                                    onClick={() => setOpenIndex(isOpen ? null : index)}
                                    aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.label} menu`}
                                    aria-expanded={isOpen}
                                    aria-controls={`mobile-nav-panel-${index}`}
                                >
                                <ChevronDown
                                    size={18}
                                    className={clsx('transition-transform', isOpen && 'rotate-180')}
                                    style={activeColorStyle ?? undefined}
                                    aria-hidden="true"
                                />
                                </button>
                            ) : null}
                        </div>

                        {item.children && (
                            <div
                                id={`mobile-nav-panel-${index}`}
                                className={clsx(
                                    'overflow-hidden transition-all',
                                    isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
                                )}
                            >
                                <div className="space-y-4 pb-4 pl-4">
                                    {item.children?.map((group, g) => (
                                        <div key={g}>
                                            <Link
                                                href={group.headingUrl ?? group.href ?? '#'}
                                                className={clsx(
                                                    'mb-2 block text-sm font-semibold',
                                                    tokens.textPrimary,
                                                )}
                                                onClick={() => setIsDrawerOpen(false)}
                                            >
                                                {group.heading}
                                            </Link>

                                            {group.submenu?.map((sub, s) => (
                                                <Link
                                                    key={s}
                                                    href={sub.href}
                                                    className={clsx(
                                                        'block py-1 text-sm',
                                                        tokens.textSecondary,
                                                    )}
                                                    onClick={() => setIsDrawerOpen(false)}
                                                >
                                                    {sub.label}
                                                </Link>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </nav>
    );
};

const MobileNavigationDrawer = ({
    isOpen,
    setIsDrawerOpen,
    menuData,
    Link,
    tokens,
    BUSINESS_NAME,
    businessDetails,
    socialLinks,
    utilityLinks,
    contactDetails,
    resolvedSearchRenderer,
    searchDisabled,
    utilityDisplayMode,
    routeActiveIndex,
    topLevelActiveStyle,
    topLevelActiveColor,
    topLevelLinkStyles,
    logoLayout,
}: {
    isOpen: boolean;
    setIsDrawerOpen: (v: boolean) => void;
    menuData: HeaderNavMenuItem[];
    Link: ComponentType<LinkProps>;
    tokens: ThemeTokenSet;
    BUSINESS_NAME?: string;
    businessDetails?: {
        branding?: { headerLogoUrl?: string };
    };
    socialLinks: MegaNavUtilityLink[];
    utilityLinks: MegaNavUtilityLink[];
    contactDetails?: MegaNavContactDetails;
    resolvedSearchRenderer?: (args: {
        className?: string;
        isMobile?: boolean;
        onNavigate?: () => void;
    }) => React.ReactNode;
    searchDisabled?: boolean;
    utilityDisplayMode: MegaNavUtilityDisplayMode;
    routeActiveIndex: number | null;
    topLevelActiveStyle: MegaNavTopLevelActiveStyle;
    topLevelActiveColor?: string;
    topLevelLinkStyles: TopLevelLinkStyleSet;
    logoLayout: keyof typeof logoLayoutTokens;
}) => {
    const contactLinks = buildContactLinks(contactDetails);
    const logoConfig = logoLayoutTokens[logoLayout];
    const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setPortalElement(document.body);
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const previousBodyOverflow = document.body.style.overflow;
        const previousDocumentOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousDocumentOverflow;
        };
    }, [isOpen]);

    const drawer = (
        <>
            <div
                className={clsx(
                    'fixed inset-0 transition-opacity duration-300',
                    tokens.overlay,
                    isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
                )}
                onClick={() => setIsDrawerOpen(false)}
                style={{ zIndex: 80 }}
            />

            <aside
                className={clsx(
                    'fixed inset-y-0 right-0 flex w-full max-w-sm flex-col transition-transform duration-300',
                    !isOpen && 'pointer-events-none',
                    tokens.dropdownBg,
                    isOpen ? 'translate-x-0' : 'translate-x-full',
                )}
                style={{ zIndex: 90, height: '100dvh', minHeight: '100svh' }}
                aria-label="Navigation drawer"
                aria-hidden={!isOpen}
                aria-modal={isOpen ? true : undefined}
                inert={!isOpen ? true : undefined}
                role="dialog"
            >
                <div
                    className={clsx(
                        'flex shrink-0 items-center justify-between border-b p-4',
                        tokens.border,
                    )}
                >
                    <Link href="/" className="flex items-center">
                        {businessDetails?.branding?.headerLogoUrl ? (
                            <img
                                src={businessDetails.branding.headerLogoUrl}
                                alt={BUSINESS_NAME}
                                className={clsx(
                                    'w-auto object-contain',
                                    logoConfig.mobileLogoHeight,
                                )}
                            />
                        ) : (
                            <span className={clsx('text-lg font-semibold', tokens.textPrimary)}>
                                {BUSINESS_NAME}
                            </span>
                        )}
                    </Link>

                    <button
                        onClick={() => setIsDrawerOpen(false)}
                        className={clsx('rounded-md p-2', tokens.hoverBg)}
                        aria-label="Close menu"
                    >
                        <X size={20} className={tokens.textPrimary} />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                    {!searchDisabled ? (
                        <div className="mb-4">
                            {resolvedSearchRenderer ? (
                                resolvedSearchRenderer({
                                    isMobile: true,
                                    className: 'w-full',
                                    onNavigate: () => setIsDrawerOpen(false),
                                })
                            ) : (
                                <SearchFallback tokens={tokens} />
                            )}
                        </div>
                    ) : null}

                    <MobileMegaMenu
                        menuData={menuData}
                        Link={Link}
                        setIsDrawerOpen={setIsDrawerOpen}
                        tokens={tokens}
                        routeActiveIndex={routeActiveIndex}
                        topLevelActiveStyle={topLevelActiveStyle}
                        topLevelActiveColor={topLevelActiveColor}
                        topLevelLinkStyles={topLevelLinkStyles}
                    />

                    <MobileUtilitySection
                        socialLinks={socialLinks}
                        utilityLinks={utilityLinks}
                        contactLinks={contactLinks}
                        Link={Link}
                        tokens={tokens}
                        onNavigate={() => setIsDrawerOpen(false)}
                        utilityDisplayMode={utilityDisplayMode}
                    />
                </div>
            </aside>
        </>
    );

    return portalElement ? createPortal(drawer, portalElement) : drawer;
};

const MegaNav = ({
    BUSINESS_NAME = 'Business',
    businessDetails,
    Link,
    headerNavMenuData,
    theme = 'light',
    layout = 'md',
    position = 'sticky',
    socialLinks = [],
    contactDetails,
    utilityLinks = [],
    search,
    renderSearch,
    cta,
    showMobileActions = true,
    customTopContent = false,
    topContentLeft,
    topContentRight,
    topContentClassName,
    utilityDisplayMode = 'icon-label',
    hideUtilityBarOnScroll = true,
    utilityBarBackgroundClassName,
    utilityBarTextColor,
    currentPathname: controlledPathname,
    logoLayout = 'default',
    topLevelActiveColor,
    topLevelActiveStyle = 'underline',
    topLevelLinkStyleOverrides,
    transparentBackground,
}: MegaNavProps & { layout?: LayoutKey }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [_intentIndex, setIntentIndex] = useState<number | null>(null);
    const [visibleIndex, setVisibleIndex] = useState<number | null>(null);
    const [isUtilityBarCollapsed, setIsUtilityBarCollapsed] = useState(false);
    const [isTransparentBackgroundScrolled, setIsTransparentBackgroundScrolled] = useState(false);
    const [isDesktopViewport, setIsDesktopViewport] = useState(false);
    const [isCompactSearchOpen, setIsCompactSearchOpen] = useState(false);
    const [hasMounted, setHasMounted] = useState(false);
    const [currentPathname, setCurrentPathname] = useState('/');

    const openTimer = useRef<number | null>(null);
    const closeTimer = useRef<number | null>(null);
    const compactSearchRef = useRef<HTMLDivElement | null>(null);
    const lastScrollYRef = useRef(0);
    const tickingRef = useRef(false);
    const collapsedRef = useRef(false);
    const transparentScrolledRef = useRef(false);

    const tokens = themeTokens[theme];
    const layoutConfig = layoutTokens[layout];
    const logoConfig = logoLayoutTokens[logoLayout];
    const logo = businessDetails?.branding?.headerLogoUrl ?? '';
    const contactLinks = buildContactLinks(contactDetails);
    const topLevelLinkStyles = getTopLevelLinkStyles(theme, topLevelLinkStyleOverrides);
    const transparentBackgroundConfig = useMemo(
        () => resolveTransparentBackgroundConfig(transparentBackground),
        [transparentBackground],
    );
    const {
        enabled: transparentBackgroundEnabled,
        mobile: transparentBackgroundMobile,
        desktop: transparentBackgroundDesktop,
        scrollThreshold: transparentBackgroundScrollThreshold,
    } = transparentBackgroundConfig;
    const usesResponsiveTransparentBackground =
        transparentBackgroundEnabled &&
        transparentBackgroundMobile !== transparentBackgroundDesktop;

    const headerPositionClass = {
        sticky: 'sticky top-0',
        fixed: 'fixed top-0 left-0 right-0',
        static: 'relative',
    }[position];

    useEffect(() => {
        if (!usesResponsiveTransparentBackground) return;

        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const updateViewport = () => setIsDesktopViewport(mediaQuery.matches);

        updateViewport();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', updateViewport);
        } else {
            mediaQuery.addListener(updateViewport);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', updateViewport);
            } else {
                mediaQuery.removeListener(updateViewport);
            }
        };
    }, [usesResponsiveTransparentBackground]);

    useEffect(() => {
        if (!isCompactSearchOpen) return;

        const frame = window.requestAnimationFrame(() => {
            const input = compactSearchRef.current?.querySelector<
                HTMLInputElement | HTMLTextAreaElement
            >('input, textarea');

            input?.focus();
        });

        return () => window.cancelAnimationFrame(frame);
    }, [isCompactSearchOpen]);

    useEffect(() => {
        if (!isCompactSearchOpen) return;

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;

            if (target && compactSearchRef.current?.contains(target)) return;

            setIsCompactSearchOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown, {
            passive: true,
        });

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [isCompactSearchOpen]);

    useEffect(() => {
        const shouldTrackTransparentBackground = transparentBackgroundEnabled;
        const shouldTrackScroll = hideUtilityBarOnScroll || shouldTrackTransparentBackground;

        if (!shouldTrackScroll) {
            collapsedRef.current = false;
            setIsUtilityBarCollapsed(false);
            transparentScrolledRef.current = false;
            setIsTransparentBackgroundScrolled(false);
            return;
        }

        const TOP_LOCK_ZONE = 20;
        const HIDE_THRESHOLD = 10;
        const SHOW_THRESHOLD = 8;

        const updateTransparentBackground = (currentY: number) => {
            if (!shouldTrackTransparentBackground) return;

            const nextIsScrolled = currentY > transparentBackgroundScrollThreshold;

            if (transparentScrolledRef.current === nextIsScrolled) return;

            transparentScrolledRef.current = nextIsScrolled;
            setIsTransparentBackgroundScrolled(nextIsScrolled);
        };

        lastScrollYRef.current = window.scrollY;
        updateTransparentBackground(window.scrollY);

        if (hideUtilityBarOnScroll) {
            collapsedRef.current = window.scrollY > 24;
            setIsUtilityBarCollapsed(collapsedRef.current);
        } else {
            collapsedRef.current = false;
            setIsUtilityBarCollapsed(false);
        }

        const updateState = () => {
            const currentY = window.scrollY;
            const previousY = lastScrollYRef.current;
            const delta = currentY - previousY;

            updateTransparentBackground(currentY);

            if (!hideUtilityBarOnScroll) {
                lastScrollYRef.current = currentY;
                tickingRef.current = false;
                return;
            }

            if (currentY <= TOP_LOCK_ZONE) {
                if (collapsedRef.current) {
                    collapsedRef.current = false;
                    setIsUtilityBarCollapsed(false);
                }
                lastScrollYRef.current = currentY;
                tickingRef.current = false;
                return;
            }

            if (!collapsedRef.current && delta > HIDE_THRESHOLD) {
                collapsedRef.current = true;
                setIsUtilityBarCollapsed(true);
                lastScrollYRef.current = currentY;
                tickingRef.current = false;
                return;
            }

            if (collapsedRef.current && delta < -SHOW_THRESHOLD) {
                collapsedRef.current = false;
                setIsUtilityBarCollapsed(false);
                lastScrollYRef.current = currentY;
                tickingRef.current = false;
                return;
            }

            lastScrollYRef.current = currentY;
            tickingRef.current = false;
        };

        const handleScroll = () => {
            if (tickingRef.current) return;
            tickingRef.current = true;
            window.requestAnimationFrame(updateState);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [
        hideUtilityBarOnScroll,
        transparentBackgroundEnabled,
        transparentBackgroundScrollThreshold,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        setHasMounted(true);

        const updatePathname = () => {
            setCurrentPathname(getWindowPathname());
        };

        updatePathname();

        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function (...args) {
            const result = originalPushState.apply(this, args as Parameters<History['pushState']>);
            window.requestAnimationFrame(updatePathname);
            return result;
        };

        window.history.replaceState = function (...args) {
            const result = originalReplaceState.apply(
                this,
                args as Parameters<History['replaceState']>,
            );
            window.requestAnimationFrame(updatePathname);
            return result;
        };

        window.addEventListener('popstate', updatePathname);

        return () => {
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
            window.removeEventListener('popstate', updatePathname);
        };
    }, []);

    const activePathname = controlledPathname
        ? normalizePathname(controlledPathname)
        : currentPathname;

    const routeActiveIndex = useMemo(() => {
        if (!hasMounted && !controlledPathname) return null;

        return getActiveMenuIndex(activePathname, headerNavMenuData);
    }, [activePathname, controlledPathname, hasMounted, headerNavMenuData]);

    const clearTimers = () => {
        if (openTimer.current) {
            window.clearTimeout(openTimer.current);
            openTimer.current = null;
        }
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    const handleItemEnter = (index: number) => {
        clearTimers();
        setIntentIndex(index);

        const item = headerNavMenuData[index];
        if (!item?.children || item.children.length === 0) return;

        openTimer.current = window.setTimeout(() => {
            setVisibleIndex(index);
        }, 90);
    };

    const handleRegionLeave = () => {
        clearTimers();
        closeTimer.current = window.setTimeout(() => {
            setIntentIndex(null);
            setVisibleIndex(null);
        }, 140);
    };

    const handleRegionEnter = () => {
        clearTimers();
    };

    const handleCompactSearchBlur = () => {
        window.setTimeout(() => {
            const activeElement = document.activeElement;

            if (!activeElement || !compactSearchRef.current?.contains(activeElement)) {
                setIsCompactSearchOpen(false);
            }
        }, 0);
    };

    const isDropdownVisible =
        visibleIndex !== null && !!headerNavMenuData[visibleIndex]?.children?.length;

    const hasCustomTopContent = customTopContent && (!!topContentLeft || !!topContentRight);
    const showDesktopUtilityBar =
        hasCustomTopContent ||
        contactLinks.length > 0 ||
        socialLinks.length > 0 ||
        utilityLinks.length > 0;
    const utilityTextClassName = utilityBarTextColor
        ? utilityBarTextColor === 'white'
            ? 'text-white'
            : 'text-black'
        : theme === 'dark'
          ? 'text-white'
          : 'text-black';
    const isTransparentBackgroundViewportEnabled =
        transparentBackgroundEnabled &&
        (transparentBackgroundMobile === transparentBackgroundDesktop
            ? transparentBackgroundMobile
            : hasMounted
              ? isDesktopViewport
                  ? transparentBackgroundDesktop
                  : transparentBackgroundMobile
              : false);
    const isHeaderTransparent =
        isTransparentBackgroundViewportEnabled && !isTransparentBackgroundScrolled;
    const defaultHeaderBackgroundClassName = isUtilityBarCollapsed
        ? tokens.scrolledHeaderBg
        : tokens.headerBg;
    const headerBackgroundClassName = isHeaderTransparent
        ? transparentBackgroundConfig.topClassName
        : (transparentBackgroundConfig.scrolledClassName ?? defaultHeaderBackgroundClassName);
    const headerBorderClassName = isHeaderTransparent
        ? (transparentBackgroundConfig.borderClassName ?? 'border-transparent')
        : (transparentBackgroundConfig.scrolledBorderClassName ?? tokens.border);
    const utilityBarResolvedBackgroundClassName = isHeaderTransparent
        ? (transparentBackgroundConfig.utilityBarTopClassName ??
          transparentBackgroundConfig.topClassName)
        : (transparentBackgroundConfig.utilityBarScrolledClassName ??
          utilityBarBackgroundClassName ??
          tokens.utilityBg);
    const utilityBarResolvedBorderClassName = isHeaderTransparent
        ? (transparentBackgroundConfig.borderClassName ?? 'border-transparent')
        : (transparentBackgroundConfig.scrolledBorderClassName ?? tokens.border);

    const searchDisabled = search === false;

    const resolvedSearchRenderer = searchDisabled
        ? null
        : renderSearch
          ? renderSearch
          : search?.endpoint
            ? ({
                  className,
                  isMobile,
                  onNavigate,
              }: {
                  className?: string;
                  isMobile?: boolean;
                  onNavigate?: () => void;
              }) => (
                  <MegaNavSearch
                      className={className}
                      isMobile={isMobile}
                      onNavigate={onNavigate}
                      Link={Link}
                      itemsEndpoint={search.endpoint}
                      placeholder={search.placeholder}
                      minChars={search.minChars}
                      debounceMs={search.debounceMs}
                      queryParam={search.queryParam}
                      limitParam={search.limitParam}
                      limit={search.limit}
                      resultsPath={search.resultsPath}
                  />
              )
            : null;

    return (
        <header
            className={clsx(
                headerPositionClass,
                'top-0 z-50 border-b transition-colors duration-300',
                headerBackgroundClassName,
                headerBorderClassName,
            )}
        >
            <div
                className="relative"
                onMouseEnter={handleRegionEnter}
                onMouseLeave={handleRegionLeave}
            >
                {showDesktopUtilityBar && (
                    <div
                        className={clsx(
                            'hidden overflow-hidden border-b transition-all duration-300 md:block',
                            utilityBarResolvedBorderClassName,
                            utilityBarResolvedBackgroundClassName,
                            isUtilityBarCollapsed ? 'max-h-0 opacity-0' : 'max-h-12 opacity-100',
                        )}
                        aria-hidden={isUtilityBarCollapsed}
                    >
                        <div
                            className={clsx(
                                'mx-auto flex min-h-8 items-center justify-between gap-3',
                                layoutConfig.desktopContentWidth,
                                layoutConfig.desktopContentPadding,
                            )}
                        >
                            {hasCustomTopContent ? (
                                <>
                                    <div
                                        className={clsx(
                                            'flex min-w-0 items-center gap-2 text-sm',
                                            utilityTextClassName,
                                            topContentClassName,
                                        )}
                                    >
                                        {topContentLeft}
                                    </div>

                                    <div
                                        className={clsx(
                                            'flex min-w-0 items-center justify-end gap-2 text-sm',
                                            utilityTextClassName,
                                            topContentClassName,
                                        )}
                                    >
                                        {topContentRight}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex flex-wrap items-center gap-1">
                                        {contactLinks.map((item) => (
                                            <UtilityLinkItem
                                                key={`${item.label}-${item.href}`}
                                                item={item}
                                                Link={Link}
                                                tokens={tokens}
                                                displayMode={utilityDisplayMode}
                                                textClassName={utilityTextClassName}
                                            />
                                        ))}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-1">
                                        {utilityLinks.map((item) => (
                                            <UtilityLinkItem
                                                key={`${item.label}-${item.href}`}
                                                item={item}
                                                Link={Link}
                                                tokens={tokens}
                                                displayMode={utilityDisplayMode}
                                                textClassName={utilityTextClassName}
                                            />
                                        ))}
                                        {socialLinks.map((item) => (
                                            <UtilityLinkItem
                                                key={`${item.label}-${item.href}`}
                                                item={item}
                                                Link={Link}
                                                tokens={tokens}
                                                displayMode={utilityDisplayMode}
                                                textClassName={utilityTextClassName}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div
                    className={clsx(
                        'mx-auto flex min-w-0 items-center gap-2 sm:gap-3 lg:gap-4',
                        logoConfig.mainBarMinHeight,
                        layoutConfig.desktopContentWidth,
                        layoutConfig.desktopContentPadding,
                    )}
                >
                    <div className="flex min-w-0 shrink items-center space-x-2 sm:space-x-3">
                        <button
                            className={clsx(
                                'rounded-md p-2 md:hidden',
                                tokens.hoverBg,
                                tokens.textPrimary,
                            )}
                            onClick={() => setIsDrawerOpen(true)}
                            aria-label="Open menu"
                        >
                            <AlignJustify size={20} />
                        </button>

                        <Link href="/" className="flex min-w-0 items-center">
                            {logo ? (
                                <img
                                    src={logo}
                                    alt={BUSINESS_NAME}
                                    className={clsx(
                                        'w-auto object-contain',
                                        showMobileActions
                                            ? 'max-w-[32vw] sm:max-w-none'
                                            : 'max-w-none',
                                        logoConfig.desktopLogoHeight,
                                    )}
                                />
                            ) : (
                                <span
                                    className={clsx(
                                        'text-lg font-semibold',
                                        showMobileActions ? 'truncate' : null,
                                        tokens.textPrimary,
                                    )}
                                >
                                    {BUSINESS_NAME}
                                </span>
                            )}
                        </Link>
                    </div>

                    <div className="hidden h-full min-w-0 flex-1 md:block">
                        <MegaMenu
                            menuData={headerNavMenuData}
                            activeIndex={visibleIndex}
                            routeActiveIndex={routeActiveIndex}
                            handleToggle={handleItemEnter}
                            Link={Link}
                            tokens={tokens}
                            topLevelActiveStyle={topLevelActiveStyle}
                            topLevelActiveColor={topLevelActiveColor}
                            topLevelLinkStyles={topLevelLinkStyles}
                        />
                    </div>

                    <div
                        className={clsx(
                            'ml-auto min-w-0 shrink-0 items-center gap-1.5 sm:gap-2 lg:gap-3',
                            showMobileActions ? 'flex' : 'hidden md:flex',
                        )}
                    >
                        {!searchDisabled ? (
                            <>
                                <div className="relative order-2 h-9 w-9 shrink-0 md:order-none min-[1200px]:hidden">
                                    <div
                                        ref={compactSearchRef}
                                        className="relative h-full w-full"
                                        onBlur={handleCompactSearchBlur}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Escape') {
                                                setIsCompactSearchOpen(false);
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className={clsx(
                                                'inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                                                tokens.border,
                                                tokens.searchBg,
                                                tokens.textPrimary,
                                                tokens.hoverBg,
                                            )}
                                            onClick={() => setIsCompactSearchOpen(true)}
                                            aria-label="Open search"
                                            aria-expanded={isCompactSearchOpen}
                                        >
                                            <Search size={17} aria-hidden="true" />
                                        </button>

                                        {isCompactSearchOpen ? (
                                            <div className="absolute right-0 top-1/2 z-50 w-[220px] -translate-y-1/2 rounded-full shadow-[0_14px_35px_rgba(15,23,42,0.22)] ring-1 ring-black/5">
                                                {resolvedSearchRenderer ? (
                                                    resolvedSearchRenderer({
                                                        className: 'w-full',
                                                        isMobile: false,
                                                    })
                                                ) : (
                                                    <SearchFallback tokens={tokens} />
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>

                                <div className="hidden min-w-[170px] w-[190px] lg:w-[220px] min-[1200px]:block xl:w-[260px] 2xl:w-[300px]">
                                    {resolvedSearchRenderer ? (
                                        resolvedSearchRenderer({
                                            className: 'w-full',
                                            isMobile: false,
                                        })
                                    ) : (
                                        <SearchFallback tokens={tokens} />
                                    )}
                                </div>
                            </>
                        ) : null}

                        {cta ? (
                            <Link
                                href={cta.href}
                                target={cta.external ? '_blank' : undefined}
                                rel={cta.external ? 'noreferrer noopener' : undefined}
                                className={clsx(
                                    'order-1 inline-flex h-9 max-w-[34vw] shrink-0 items-center whitespace-nowrap rounded px-3 text-xs font-semibold transition-colors sm:max-w-none sm:px-4 sm:text-sm md:order-none md:px-3 md:text-[13px] lg:px-4 lg:text-sm',
                                    'bg-[#2563eb]  hover:bg-[#1d4ed8]',
                                    // Default blue admin CTA. Override by wrapping/styling the component if needed.
                                )}
                                style={{ color: '#fff' }}
                            >
                                <span className="truncate">{cta.label}</span>
                            </Link>
                        ) : null}
                    </div>
                </div>

                <div
                    className={clsx(
                        'absolute left-0 right-0 top-full z-40 transition-opacity duration-300 ease-out',
                        isDropdownVisible
                            ? 'pointer-events-auto opacity-100'
                            : 'pointer-events-none opacity-0',
                    )}
                >
                    {isDropdownVisible && (
                        <div
                            className={clsx(tokens.dropdownBg, 'border-t shadow-lg', tokens.border)}
                        >
                            <div className={clsx('mx-auto', layoutConfig.desktopContentWidth)}>
                                <div className={clsx(layoutConfig.desktopContentPadding, 'py-4')}>
                                    <DesktopDropDownMenu
                                        submenuscontent={headerNavMenuData[visibleIndex!].children!}
                                        Link={Link}
                                        tokens={tokens}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <MobileNavigationDrawer
                isOpen={isDrawerOpen}
                setIsDrawerOpen={setIsDrawerOpen}
                menuData={headerNavMenuData}
                Link={Link}
                tokens={tokens}
                BUSINESS_NAME={BUSINESS_NAME}
                businessDetails={businessDetails}
                socialLinks={socialLinks}
                utilityLinks={utilityLinks}
                contactDetails={contactDetails}
                resolvedSearchRenderer={resolvedSearchRenderer ?? undefined}
                searchDisabled={searchDisabled}
                utilityDisplayMode={utilityDisplayMode}
                routeActiveIndex={routeActiveIndex}
                topLevelActiveStyle={topLevelActiveStyle}
                topLevelActiveColor={topLevelActiveColor}
                topLevelLinkStyles={topLevelLinkStyles}
                logoLayout={logoLayout}
            />
        </header>
    );
};

export default MegaNav;
